import React, { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { createPortal } from 'react-dom';

import { BellIcon, IconClose } from '../../assets/icons/icons';
import { useGetUserNotificationsQuery } from '../../entities/notifications/api/notificationsApi';
import { useSeenNotifications } from '../../entities/notifications/model/seen';
import { formatYekb, isActive, useNowYekb } from '../../entities/notifications/model/window';
import type { AppNotification } from '../../entities/notifications/model/types';
import type { AuthUser } from '../../types/user';
import styles from './NotificationPopups.module.scss';

const POLL_MS = 60_000;

const MAX_SHOWN = 3;

interface NotificationPopupsProps {
    user?: AuthUser | null;
}

export const NotificationPopups: FC<NotificationPopupsProps> = ({ user }) => {
    const userId = user?.id ?? user?.email;

    const { data } = useGetUserNotificationsQuery(undefined, {
        skip: !userId,
        pollingInterval: POLL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
    });

    const { seen, markSeen } = useSeenNotifications(userId);
    const now = useNowYekb();

    const items = data?.items;

    const fresh = useMemo(
        () => (items ?? []).filter((item) => isActive(item, now) && !seen.has(item.id)),
        [items, now, seen],
    );

    const [queue, setQueue] = useState<AppNotification[]>([]);

    useEffect(() => {
        if (!fresh.length) return;

        setQueue((prev) => {
            const known = new Set(prev.map((item) => item.id));
            const added = fresh.filter((item) => !known.has(item.id));
            return added.length ? prev.concat(added) : prev;
        });

        markSeen(fresh.map((item) => item.id));
    }, [fresh, markSeen]);

    const visible = useMemo(() => queue.filter((item) => isActive(item, now)), [queue, now]);

    const dismiss = (id: string): void => setQueue((prev) => prev.filter((item) => item.id !== id));

    if (!visible.length) return null;

    const shown = visible.slice(0, MAX_SHOWN);
    const rest = visible.length - shown.length;

    return createPortal(
        <div className={styles.stack} role="status" aria-live="polite">
            {shown.map((item) => (
                <article key={item.id} className={styles.card}>
                    <span className={styles.icon}>
                        <BellIcon />
                    </span>

                    <div className={styles.copy}>
                        <p>{item.text}</p>
                        <small>до {formatYekb(item.endsAt)}</small>
                    </div>

                    <button
                        type="button"
                        className={styles.close}
                        aria-label="Скрыть уведомление"
                        onClick={() => dismiss(item.id)}
                    >
                        <IconClose />
                    </button>
                </article>
            ))}

            {rest > 0 && (
                <p className={styles.more}>
                    Ещё {rest} — смотрите в колокольчике
                </p>
            )}
        </div>,
        document.body,
    );
};